/**
 * Centralized URL service for generating media, post, and report URLs
 * based on user role (admin, editor, client)
 */

import { API_CONFIG } from '@/config/api';

export enum UserRole {
  ADMIN = 'admin',
  EDITOR = 'editor',
  CLIENT = 'client'
}

export type UserRoleValue = 'admin' | 'editor' | 'client';

export interface MediaUrlOptions {
  role: UserRoleValue;
  clientId: string;
  mediaId: number;
  mediaType: 'image' | 'video';
  token?: string;
  variant?: 'thumbnail' | 'stream' | 'download';
}

export interface PostUrlOptions {
  role: UserRoleValue;
  clientId: string;
  postId: string;
  token?: string;
}

export interface ReportUrlOptions {
  role: UserRoleValue;
  clientId: string;
  reportType: string;
  token?: string;
}

class UrlService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.URL;
  }

  /**
   * Generate media URL based on role and context
   */
  getMediaUrl(options: MediaUrlOptions): string {
    const { role, clientId, mediaId, mediaType, token, variant = 'thumbnail' } = options;

    switch (role) {
      case 'admin':
        return this.getAdminMediaUrl(clientId, mediaId, mediaType, variant);

      case 'editor':
        return this.getEditorMediaUrl(clientId, mediaId, mediaType, variant, token);

      case 'client':
        return this.getClientMediaUrl(clientId, mediaId, mediaType, variant, token);

      default:
        throw new Error(`Unknown role: ${role}`);
    }
  }

  /**
   * Generate post URL based on role and context
   */
  getPostUrl(options: PostUrlOptions): string {
    const { role, clientId, postId, token } = options;

    switch (role) {
      case 'admin':
        return `${this.baseUrl}/posts/${postId}`;

      case 'editor':
        return `${this.baseUrl}/editor/${clientId}/posts/${postId}${token ? `?access_token=${encodeURIComponent(token)}` : ''}`;

      case 'client':
        return `${this.baseUrl}/client/${clientId}/posts/${postId}${token ? `?access_token=${encodeURIComponent(token)}` : ''}`;

      default:
        throw new Error(`Unknown role: ${role}`);
    }
  }

  /**
   * Generate report URL based on role and context
   */
  getReportUrl(options: ReportUrlOptions): string {
    const { role, clientId, reportType, token } = options;

    switch (role) {
      case 'admin':
        return `${this.baseUrl}/reports/${clientId}/${reportType}`;

      case 'editor':
        return `${this.baseUrl}/editor/${clientId}/reports/${reportType}${token ? `?access_token=${encodeURIComponent(token)}` : ''}`;

      case 'client':
        return `${this.baseUrl}/client/${clientId}/reports/${reportType}${token ? `?access_token=${encodeURIComponent(token)}` : ''}`;

      default:
        throw new Error(`Unknown role: ${role}`);
    }
  }

  // Private helper methods for each role

  private getAdminMediaUrl(
    clientId: string,
    mediaId: number,
    mediaType: 'image' | 'video',
    variant: string
  ): string {
    if (mediaType === 'image') {
      return `${this.baseUrl}/media/${clientId}/image/${mediaId}`;
    } else {
      if (variant === 'thumbnail') {
        return `${this.baseUrl}/media/${clientId}/video/${mediaId}/thumbnail`;
      } else if (variant === 'stream') {
        return `${this.baseUrl}/media/${clientId}/video/${mediaId}/stream`;
      } else if (variant === 'download') {
        return `${this.baseUrl}/media/${clientId}/video/${mediaId}/download`;
      }
      return `${this.baseUrl}/media/${clientId}/video/${mediaId}/stream`;
    }
  }

  private getEditorMediaUrl(
    clientId: string,
    mediaId: number,
    mediaType: 'image' | 'video',
    variant: string,
    token?: string
  ): string {
    const tokenParam = token ? `?access_token=${encodeURIComponent(token)}` : '';

    if (mediaType === 'image') {
      return `${this.baseUrl}/editor/${clientId}/image/${mediaId}${tokenParam}`;
    } else {
      if (variant === 'thumbnail') {
        return `${this.baseUrl}/editor/${clientId}/video/${mediaId}/thumbnail${tokenParam}`;
      } else if (variant === 'stream') {
        return `${this.baseUrl}/editor/${clientId}/video/${mediaId}/stream${tokenParam}`;
      } else if (variant === 'download') {
        return `${this.baseUrl}/editor/${clientId}/video/${mediaId}/download${tokenParam}`;
      }
      return `${this.baseUrl}/editor/${clientId}/video/${mediaId}/stream${tokenParam}`;
    }
  }

  private getClientMediaUrl(
    clientId: string,
    mediaId: number,
    mediaType: 'image' | 'video',
    variant: string,
    token?: string
  ): string {
    const tokenParam = token ? `?access_token=${encodeURIComponent(token)}` : '';

    if (mediaType === 'image') {
      return `${this.baseUrl}/client/${clientId}/image/${mediaId}${tokenParam}`;
    } else {
      if (variant === 'thumbnail') {
        return `${this.baseUrl}/client/${clientId}/video/${mediaId}/thumbnail${tokenParam}`;
      } else if (variant === 'stream') {
        return `${this.baseUrl}/client/${clientId}/video/${mediaId}/stream${tokenParam}`;
      } else if (variant === 'download') {
        return `${this.baseUrl}/client/${clientId}/video/${mediaId}/download${tokenParam}`;
      }
      return `${this.baseUrl}/client/${clientId}/video/${mediaId}/stream${tokenParam}`;
    }
  }

  /**
   * Get the API base URL for building custom URLs
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Build URL with query parameters
   */
  buildUrlWithParams(baseUrl: string, params: Record<string, string | number | boolean>): string {
    const url = new URL(baseUrl, this.baseUrl);
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });
    return url.toString();
  }
}

export const urlService = new UrlService();
